// File: src/components/modals/EditStudentModal.jsx
// Modal to edit limited student fields (including vehicle preference)

import React, { useState, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { X, User, Phone, MapPin, CheckCircle, AlertCircle, Save, Car } from 'lucide-react';
import { studentAPI } from '../../services/api';

const EditStudentModal = ({ student, onClose, onSuccess }) => {
  const { getAccessTokenSilently } = useAuth0();

  // Form state
  const [formData, setFormData] = useState({
    progressLevel: 1,
    status: '',
    phone: '',
    pickupAddress: '',
    pickupPostcode: '',
    dropoffAddress: '',
    dropoffPostcode: '',
    vehicleOption: 'Automatic'
  });

  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  // Load student data on mount
  useEffect(() => {
    if (student) {
      loadStudentData();
    }
  }, [student]);

  const loadStudentData = async () => {
    setIsLoading(true);
    setError(null);

    try {
      // If we need to fetch fresh data, uncomment this:
      // const response = await studentAPI.getStudentById(student.id, getAccessTokenSilently);
      // const studentData = response.data;

      // For now, use the student prop directly
      setFormData({
        progressLevel: student.progressLevel ?? 1,
        status: student.status || 'active',
        phone: student.phone || '',
        pickupAddress: student.pickupAddress || '',
        pickupPostcode: student.pickupPostcode || '',
        dropoffAddress: student.dropoffAddress || '',
        dropoffPostcode: student.dropoffPostcode || '',
        vehicleOption: student.vehicleOption || 'Automatic'
      });
    } catch (err) {
      console.error('Error loading student data:', err);
      setError('Failed to load student data');
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleStatusToggle = (statusValue) => {
    setFormData(prev => ({
      ...prev,
      status: statusValue
    }));
  };

  // ✅ NEW: Handle vehicle option toggle
  const handleVehicleToggle = (vehicleValue) => {
    setFormData(prev => ({
      ...prev,
      vehicleOption: vehicleValue
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    setError(null);
    setSuccessMessage(null);

    try {
      // Prepare updates object (only send changed fields)
      const updates = {};

      if (Number(formData.progressLevel) !== Number(student.progressLevel ?? 1)) {
        updates.progressLevel = Number(formData.progressLevel);
      }
      if (formData.status !== student.status) {
        updates.status = formData.status;
      }
      if (formData.phone !== student.phone) {
        updates.phone = formData.phone;
      }
      if (formData.pickupAddress !== student.pickupAddress) {
        updates.pickupAddress = formData.pickupAddress;
      }
      if (formData.pickupPostcode !== student.pickupPostcode) {
        updates.pickupPostcode = formData.pickupPostcode;
      }
      if (formData.dropoffAddress !== student.dropoffAddress) {
        updates.dropoffAddress = formData.dropoffAddress;
      }
      if (formData.dropoffPostcode !== student.dropoffPostcode) {
        updates.dropoffPostcode = formData.dropoffPostcode;
      }
      // ✅ NEW: Include vehicleOption in updates
      if (formData.vehicleOption !== student.vehicleOption) {
        updates.vehicleOption = formData.vehicleOption;
      }

      // Check if there are any changes
      if (Object.keys(updates).length === 0) {
        setError('No changes detected');
        setIsSaving(false);
        return;
      }

      console.log('Updating student with:', updates);

      const response = await studentAPI.updateStudentFields(
        student.id,
        updates,
        getAccessTokenSilently
      );

      if (response.success) {
        setSuccessMessage('Student updated successfully!');
        
        // Call onSuccess callback to refresh the list
        if (onSuccess) {
          setTimeout(() => {
            onSuccess();
            onClose();
          }, 1500);
        }
      } else {
        setError(response.message || 'Failed to update student');
      }
    } catch (err) {
      console.error('Error updating student:', err);
      setError(err.message || 'Failed to update student. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const formatPhone = (phone) => {
    if (!phone) return '';
    // Remove any non-digit characters for display
    return phone;
  };

  if (isLoading) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <div className="loading-state">
            <div className="spinner"></div>
            <p>Loading student data...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content modal-medium" onClick={(e) => e.stopPropagation()}>
        {/* Modal Header */}
        <div className="modal-header">
          <div className="modal-header-content">
            <User size={24} />
            <h2>Edit Student</h2>
          </div>
          <button className="close-button" onClick={onClose}>
            <X size={24} />
          </button>
        </div>

        {/* Modal Body */}
        <div className="modal-body">
          <form onSubmit={handleSubmit} className="edit-form">
            {/* Success Message */}
            {successMessage && (
              <div className="success-banner">
                <CheckCircle size={20} />
                <span>{successMessage}</span>
              </div>
            )}

            {/* Error Message */}
            {error && (
              <div className="error-banner">
                <AlertCircle size={20} />
                <span>{error}</span>
              </div>
            )}

            {/* Student Name (Read-only) */}
            <div className="form-section">
              <div className="form-group">
                <label className="form-label-readonly">Student Name</label>
                <input
                  type="text"
                  value={student.name || 'Unknown'}
                  disabled
                  className="form-input-readonly"
                />
              </div>
            </div>

            {/* Progress Level */}
            <div className="form-section">
              <div className="form-group">
                <label className="form-label">Progress Level</label>
                <select
                  name="progressLevel"
                  value={formData.progressLevel}
                  onChange={handleInputChange}
                  className="form-input"
                  style={{ cursor: 'pointer' }}
                >
                  {Array.from({ length: 6 }, (_, i) => i ).map(n => (
                    <option key={n} value={n}>Level {n}</option>
                  ))}
                </select>
                <p className="form-hint">
                  Current level: <strong>Level {student.progressLevel ?? 1}</strong>
                </p>
              </div>
            </div>

            {/* Status Toggles */}
            <div className="form-section">
              <label className="form-label">Status</label>
              <div className="status-toggle-group">
                <button
                  type="button"
                  className={`status-toggle-btn ${formData.status === 'active' ? 'active' : ''}`}
                  onClick={() => handleStatusToggle('active')}
                >
                  <CheckCircle size={18} />
                  Active
                </button>
                <button
                  type="button"
                  className={`status-toggle-btn ${formData.status === 'inactive' ? 'inactive' : ''}`}
                  onClick={() => handleStatusToggle('inactive')}
                >
                  <X size={18} />
                  Inactive
                </button>
                <button
                  type="button"
                  className={`status-toggle-btn ${formData.status === 'passed' ? 'passed' : ''}`}
                  onClick={() => handleStatusToggle('passed')}
                >
                  <CheckCircle size={18} />
                  Passed
                </button>
              </div>
              <p className="form-hint">Current status: <strong>{student.status === 'approve' ? 'Registered' : student.status}</strong></p>
            </div>

            {/* ✅ NEW: Vehicle Preference Toggles */}
            <div className="form-section">
              <label className="form-label">
                <Car size={16} />
                Vehicle Preference
              </label>
              <div className="status-toggle-group">
                <button
                  type="button"
                  className={`status-toggle-btn vehicle-btn ${formData.vehicleOption === 'Automatic' ? 'automatic' : ''}`}
                  onClick={() => handleVehicleToggle('Automatic')}
                >
                  <Car size={18} />
                  Automatic
                </button>
                <button
                  type="button"
                  className={`status-toggle-btn vehicle-btn ${formData.vehicleOption === 'Manual' ? 'manual' : ''}`}
                  onClick={() => handleVehicleToggle('Manual')}
                >
                  <Car size={18} />
                  Manual
                </button>
              </div>
              <p className="form-hint">
                Current preference: <strong>{student.vehicleOption || 'Not set'}</strong>
              </p>
            </div>

            {/* Phone Number */}
            <div className="form-section">
              <div className="form-group">
                <label className="form-label">
                  <Phone size={16} />
                  Phone Number
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className="form-input"
                  placeholder="+44 7423 393876"
                />
              </div>
            </div>

            {/* Pickup Address */}
            <div className="form-section">
              <label className="form-label">
                <MapPin size={16} />
                Pickup Address
              </label>
              <div className="address-group">
                <div className="form-group">
                  <label className="form-sublabel">Street Address</label>
                  <input
                    type="text"
                    name="pickupAddress"
                    value={formData.pickupAddress}
                    onChange={handleInputChange}
                    className="form-input"
                    placeholder="80 BURDEN DRIVE"
                  />
                </div>
                <div className="form-group">
                  <label className="form-sublabel">Postcode</label>
                  <input
                    type="text"
                    name="pickupPostcode"
                    value={formData.pickupPostcode}
                    onChange={handleInputChange}
                    className="form-input"
                    placeholder="SP1 3RD"
                  />
                </div>
              </div>
            </div>

            {/* Dropoff Address */}
            <div className="form-section">
              <label className="form-label">
                <MapPin size={16} />
                Dropoff Address
              </label>
              <div className="address-group">
                <div className="form-group">
                  <label className="form-sublabel">Street Address</label>
                  <input
                    type="text"
                    name="dropoffAddress"
                    value={formData.dropoffAddress}
                    onChange={handleInputChange}
                    className="form-input"
                    placeholder="80 BURDEN DRIVE"
                  />
                </div>
                <div className="form-group">
                  <label className="form-sublabel">Postcode</label>
                  <input
                    type="text"
                    name="dropoffPostcode"
                    value={formData.dropoffPostcode}
                    onChange={handleInputChange}
                    className="form-input"
                    placeholder="SP1 3RD"
                  />
                </div>
              </div>
            </div>
          </form>
        </div>

        {/* Modal Footer */}
        <div className="modal-footer">
          <button 
            type="button" 
            className="btn-secondary" 
            onClick={onClose}
            disabled={isSaving}
          >
            Cancel
          </button>
          <button 
            type="submit" 
            className="btn-primary"
            onClick={handleSubmit}
            disabled={isSaving}
          >
            {isSaving ? (
              <>
                <div className="btn-spinner"></div>
                Saving...
              </>
            ) : (
              <>
                <Save size={18} />
                Save Changes
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditStudentModal;